<template>
  <transition-group name="modal-fade">
    <div
      v-if="isVisible"
      key="modal"
      :class="modalClassNames"
      tabindex="-1"
      role="dialog"
      aria-hidden="true"
      @mouseup.left="handleModalMouseUp"
      @mousedown.left="handleModalMouseDown"
      @keydown="handleModalKeyDown"
    >
      <div class="modal-dialog modal-dialog-scrollable" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <slot v-if="header" name="header" />
            <h5 v-else class="modal-title">
              {{ title }}
            </h5>
            <button
              type="button"
              :class="closeButtonClass"
              data-dismiss="modal"
              aria-label="Close"
              @click.prevent="closeModal"
            />
          </div>
          <div class="modal-body">
            <slot name="body" />
          </div>
          <div class="modal-footer d-flex flex-row">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="isVisible"
      key="backdrop"
      class="modal-backdrop show"
    />
  </transition-group>
</template>

<script>
import get from 'lodash/get';
import { OVERLAY_OVER_NAVBAR_CLASS } from '~/constants/global';

const MODAL_CLASS_NAMES = 'modal show d-block';
const REMOVE_BODY_MODAL_CLASS_TIMEOUT = 500;

export default {
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    wrapperClass: {
      type: String,
      default: '',
    },
    // Used to customize the header content in the modal
    header: {
      type: Boolean,
      default: false,
    },
    useLightHeaderButton: {
      type: Boolean,
      default: false,
    },
    disableBackdropClick: {
      type: Boolean,
      default: false,
    },

    /**
     * Keep the class added to the body element (OVERLAY_OVER_NAVBAR_CLASS).
     * Useful if you need to open another modal immediately after closing
     * a previous one.
     */
    keepBodyModalClass: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      modalClassNames: [MODAL_CLASS_NAMES, this.wrapperClass],
      mouseDownOutside: false,
    };
  },

  computed: {
    closeButtonClass() {
      return `close ${this.useLightHeaderButton ? 'light' : ''}`;
    },
  },

  watch: {
    async isVisible(newValue) {
      if (newValue) {
        document.body.classList.add(OVERLAY_OVER_NAVBAR_CLASS);
      } else if (!this.keepBodyModalClass) {
        // Remove class after the transition has finished
        setTimeout(() => {
          document.body.classList.remove(OVERLAY_OVER_NAVBAR_CLASS);
        }, REMOVE_BODY_MODAL_CLASS_TIMEOUT);
      }
    },
  },

  methods: {
    closeModal() {
      this.mouseDownOutside = false;
      this.$emit('onClose');
    },
    handleModalMouseUp(event) {
      /**
       * The .modal class has greater z-index value than the .modal-backdrop one,
       * which is intended. Therefore when clicking "outside" the modal we are actually
       * clicking on the .modal class. This piece of code make sure that we only emit
       * the onClose event when clicking outside of the modal body which means clicking
       * on the .modal class itslef and not their inner elements.
      */

      if (this.disableBackdropClick) {
        return;
      }

      // allow user to close modal by clicking outside
      const fullClassNames = this.modalClassNames.join(' ');
      if (get(event, 'target.className', null) === fullClassNames.trim()
        && this.mouseDownOutside) {
        this.closeModal();
      } else {
        this.mouseDownOutside = false;
      }
    },
    handleModalKeyDown(event) {
      if (event.key === 'Escape' || event.keyCode === 27) {
        this.closeModal();
      }
    },
    handleModalMouseDown(event) {
      /**
       * Need to validate that the mouse down even happened outside of the modal
       * Only close modal if full even happened outside. Mouse down and then mouse up
      */
      const fullClassNames = this.modalClassNames.join(' ');
      if (get(event, 'target.className', null) === fullClassNames.trim()) {
        this.mouseDownOutside = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  @import 'node_modules/bootstrap/scss/functions';
  @import 'node_modules/bootstrap/scss/variables';
  @import '~/styles/dorado/base/base';
  @import '~/styles/dorado/abstracts/modal';
  @import '~/styles/dorado/components/modal';

  // Override font size based on dorado, there's an issue with the sass
  // variables because we are importing first the bootstrap variables and
  // the font size from bootstrap (2rem) is used to generate the font size title.
  // There's no way to fix this without an override until we import dorado
  // to the PDP page.
  .modal-title {
    font-size: 1.75rem;
  }

  // Override legacy styles
  .modal-dialog {
    left: 0 !important;
    position: relative !important;
    top: 0 !important;
  }

  .modal.show {
    z-index: 9999;
  }

  .modal-backdrop.show {
    z-index: 9998;
  }

  .modal-fade-enter-active,
  .modal-fade-leave-active {
    transition: opacity 350ms;
  }

  .modal-fade-enter,
  .modal-fade-leave-to {
    opacity: 0;
  }

  .close.light {
    position: absolute;
    right: 1rem;
    top: 1rem;
  }
</style>
